// historyService.js
// API service for job history

import { HTTPService } from './httpService';

const API = new HTTPService();

// ============================================================================
// Job History
// ============================================================================

/**
 * Get job history with filters
 * @param {number} installerId - Required installer ID
 * @param {object} filters - Optional filters
 * @param {string} filters.search - Text search across customer, dealer, VIN, vehicle
 * @param {string} filters.source - 'dealer', 'retail', 'referral'
 * @param {string} filters.status - 'collected', 'invoiced', 'paid'
 * @param {number} filters.categoryId - Filter by service category
 * @param {number} filters.serviceId - Filter by specific service
 * @param {number} filters.productId - Filter by material/product
 * @param {string} filters.startDate - YYYY-MM-DD
 * @param {string} filters.endDate - YYYY-MM-DD
 * @param {number} filters.limit - Max results (default 50)
 * @param {number} filters.offset - Pagination offset
 * @returns {Promise<{data: Array, pagination: object}>}
 */
export const getJobHistory = async (installerId, filters = {}, locationId = null) => {
    const params = {
        installerId,
        includeNoMaterials: true,
        ...filters
    };
    if (locationId && locationId !== 'all') params.locationId = locationId;
    const { data } = await API.get('/api/inventory/usage-history', params);
    return data;
};

/**
 * Get services/notes/history for a specific job (for ViewJob modal)
 * @param {number} requestId - Job/Request ID
 * @returns {Promise<{services: Array, notes: Array, history: Array}>}
 */
export const getJobDetails = async (requestId) => {
    // Fetch full request, services, notes, and state history in parallel
    const [requestRes, servicesRes, notesRes, historyRes] = await Promise.all([
        API.get(`/requestimates/fullrequest/?Id=${requestId}`),
        API.get(`/requestServices/${requestId}`),
        API.get(`/requestNotes/${requestId}`),
        API.get(`/requeststate/${requestId}`)
    ]);

    const request = requestRes.data?.[0] || null;

    // Fetch customer-facing invoice number if request has a Stripe invoice ID
    if (request && request.invoiceId) {
        try {
            const { data: invoice } = await API.get(`/invoices/${request.invoiceId}`);
            if (invoice && invoice.number) {
                request.customer_facing_invoice = invoice.number;
            }
        } catch {
            // Invoice lookup failed — fall back to whatever invoiceId has
        }
    }

    return {
        request,
        services: servicesRes.data || [],
        notes: notesRes.data || [],
        history: historyRes.data || []
    };
};

// ============================================================================
// Filter Options
// ============================================================================

/**
 * Get service categories for filter dropdown
 * Uses the global service categories endpoint
 * @param {number} installerId - Required (for future filtering)
 * @returns {Promise<Array>}
 */
export const getCategories = async (installerId) => {
    // Get all service categories
    const { data } = await API.get('/api/service-categories');
    const categories = data.data || data || [];

    // Return with consistent field names
    return categories.map(cat => ({
        category_id: cat.category_id,
        category_name: cat.category_name
    }));
};

/**
 * Get services for filter dropdown
 * @param {number} installerId - Required
 * @param {number} categoryId - Optional, filter by category
 * @returns {Promise<Array>}
 */
export const getServices = async (installerId, categoryId = null) => {
    // Use the master-services endpoint which includes category_id
    const { data } = await API.get(`/api/master-services/installer/${installerId}`);
    const services = data.data || data || [];

    // Map to expected field names
    return services.map(svc => ({
        service_id: svc.master_service_id || svc.service_id,
        service_name: svc.service_name || svc.description,
        category_id: svc.category_id,
        category_name: svc.category_name
    }));
};

/**
 * Get products/materials for filter dropdown
 * @param {number} installerId - Required
 * @param {number} categoryId - Optional, filter by category
 * @returns {Promise<Array>}
 */
export const getProducts = async (installerId, categoryId = null) => {
    const params = { installerId };
    if (categoryId) params.categoryId = categoryId;
    const { data } = await API.get('/api/products', params);
    return data.data || data;
};

// ============================================================================
// Export
// ============================================================================

/**
 * Export job history to CSV
 * @param {number} installerId - Required
 * @param {object} filters - Same filters as getJobHistory
 * @returns {Promise<Blob>} CSV file blob
 */
export const exportHistoryToCsv = async (installerId, filters = {}) => {
    const params = {
        installerId,
        includeNoMaterials: true,
        format: 'csv',
        ...filters
    };
    const response = await API.get('/api/inventory/usage-history', params);
    return response.data;
};

// Default export
const HistoryAPI = {
    getJobHistory,
    getJobDetails,
    getCategories,
    getServices,
    getProducts,
    exportHistoryToCsv
};

export default HistoryAPI;
